import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const ApiKeyStatusSchema = z.enum(['ACTIVE', 'REVOKED', 'EXPIRED']);
export type ApiKeyStatus = z.infer<typeof ApiKeyStatusSchema>;

export const ApiKeyRateLimitTierSchema = z.enum(['STANDARD', 'PREMIUM', 'INTERNAL']);
export type ApiKeyRateLimitTier = z.infer<typeof ApiKeyRateLimitTierSchema>;

export const SecurityEventKindSchema = z.enum([
  'LOGIN_SUCCESS',
  'LOGIN_FAILED',
  'LOGOUT',
  'PASSWORD_CHANGED',
  'TOKEN_REVOKED',
  'IP_NEW_LOCATION',
  'RATE_LIMIT_BREACH',
  'PERMISSION_DENIED',
  'WEBHOOK_SIGNATURE_INVALID',
  'API_KEY_USED',
  'API_KEY_REVOKED',
  'SUSPICIOUS_ACTIVITY',
]);
export type SecurityEventKind = z.infer<typeof SecurityEventKindSchema>;

export const SecurityEventSeveritySchema = z.enum(['INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export type SecurityEventSeverity = z.infer<typeof SecurityEventSeveritySchema>;

export const RolloutFlagStatusSchema = z.enum(['DISABLED', 'ROLLOUT', 'ENABLED', 'ARCHIVED']);
export type RolloutFlagStatus = z.infer<typeof RolloutFlagStatusSchema>;

export const UpstreamProviderSchema = z.enum([
  'RAZORPAY',
  'SHIPROCKET',
  'AWS_SES',
  'TWILIO',
  'META_WHATSAPP',
  'RAZORPAY_X',
  'CLOUDFLARE_R2',
  'AWS_S3',
]);
export type UpstreamProvider = z.infer<typeof UpstreamProviderSchema>;

export const UpstreamHealthGradeSchema = z.enum(['GREEN', 'YELLOW', 'RED', 'CIRCUIT_OPEN']);
export type UpstreamHealthGrade = z.infer<typeof UpstreamHealthGradeSchema>;

// ── API keys ───────────────────────────────────────────────────────────────

export const ApiKeySchema = z.object({
  id: Id,
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  ownerUserId: Id.nullable(),
  status: ApiKeyStatusSchema,
  rateLimitTier: ApiKeyRateLimitTierSchema,
  expiresAt: IsoDateTime.nullable(),
  lastUsedAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

/// Issuing an API key returns the plaintext ONCE. Subsequent reads only expose the prefix.
export const IssuedApiKeySchema = ApiKeySchema.extend({
  /// Plaintext shown once. Caller MUST persist it client-side; the server only keeps a hash.
  plaintext: z.string(),
});
export type IssuedApiKey = z.infer<typeof IssuedApiKeySchema>;

export const IssueApiKeyRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    scopes: z.array(z.string().trim().min(2).max(80)).min(1).max(60),
    ownerUserId: Id.optional(),
    rateLimitTier: ApiKeyRateLimitTierSchema.optional(),
    expiresAt: IsoDateTime.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type IssueApiKeyRequest = z.infer<typeof IssueApiKeyRequest>;

export const RevokeApiKeyRequest = z
  .object({
    reason: z.string().trim().min(2).max(500).optional(),
  })
  .strict();
export type RevokeApiKeyRequest = z.infer<typeof RevokeApiKeyRequest>;

// ── Security events ───────────────────────────────────────────────────────

export const SecurityEventSchema = z.object({
  id: Id,
  kind: SecurityEventKindSchema,
  severity: SecurityEventSeveritySchema,
  actorId: z.string().nullable(),
  actorType: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  context: z.unknown().nullable(),
  createdAt: IsoDateTime,
});
export type SecurityEvent = z.infer<typeof SecurityEventSchema>;

export const RecordSecurityEventRequest = z
  .object({
    kind: SecurityEventKindSchema,
    severity: SecurityEventSeveritySchema.optional(),
    actorId: z.string().trim().max(120).optional(),
    actorType: z.string().trim().max(40).optional(),
    ip: z.string().trim().max(60).optional(),
    userAgent: z.string().trim().max(500).optional(),
    context: z.record(z.unknown()).optional(),
  })
  .strict();
export type RecordSecurityEventRequest = z.infer<typeof RecordSecurityEventRequest>;

export const ListSecurityEventsQuery = z
  .object({
    kind: SecurityEventKindSchema.optional(),
    severity: SecurityEventSeveritySchema.optional(),
    actorId: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
  })
  .strict();
export type ListSecurityEventsQuery = z.infer<typeof ListSecurityEventsQuery>;

// ── Rollout flags ─────────────────────────────────────────────────────────

export const RolloutFlagSchema = z.object({
  id: Id,
  key: z.string(),
  description: z.string().nullable(),
  status: RolloutFlagStatusSchema,
  rolloutBps: z.number().int(),
  enabledForUserIds: z.array(Id),
  enabledForRoles: z.array(z.string()),
  variants: z.unknown().nullable(),
  createdAt: IsoDateTime,
});
export type RolloutFlag = z.infer<typeof RolloutFlagSchema>;

export const UpsertRolloutFlagRequest = z
  .object({
    key: z.string().trim().regex(/^[a-z][a-z0-9_.-]{1,80}$/, 'key must be lowercase slug'),
    description: z.string().trim().max(500).optional(),
    status: RolloutFlagStatusSchema.optional(),
    rolloutBps: z.number().int().min(0).max(10_000).optional(),
    enabledForUserIds: z.array(Id).max(10_000).optional(),
    enabledForRoles: z.array(z.string().trim().max(40)).max(20).optional(),
    variants: z.record(z.unknown()).optional(),
  })
  .strict();
export type UpsertRolloutFlagRequest = z.infer<typeof UpsertRolloutFlagRequest>;

export const EvaluateFlagQuery = z
  .object({
    key: z.string().trim().min(1).max(80),
  })
  .strict();
export type EvaluateFlagQuery = z.infer<typeof EvaluateFlagQuery>;

export const FlagEvaluationSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  reason: z.enum(['DISABLED', 'ROLLOUT_HIT', 'ROLLOUT_MISS', 'USER_ALLOWLIST', 'ROLE_ALLOWLIST', 'ENABLED', 'NOT_FOUND']),
  variants: z.unknown().nullable(),
});
export type FlagEvaluation = z.infer<typeof FlagEvaluationSchema>;

// ── Upstream health / circuit breaker ─────────────────────────────────────

export const UpstreamHealthCheckSchema = z.object({
  id: Id,
  provider: UpstreamProviderSchema,
  grade: UpstreamHealthGradeSchema,
  latencyMs: z.number().int().nullable(),
  failuresInWindow: z.number().int(),
  totalInWindow: z.number().int(),
  openedAt: IsoDateTime.nullable(),
  recoveryAt: IsoDateTime.nullable(),
  lastCheckedAt: IsoDateTime,
  lastError: z.string().nullable(),
});
export type UpstreamHealthCheck = z.infer<typeof UpstreamHealthCheckSchema>;

export const RecordProbeRequest = z
  .object({
    provider: UpstreamProviderSchema,
    success: z.boolean(),
    latencyMs: z.number().int().min(0).max(60_000).optional(),
    error: z.string().trim().max(1000).optional(),
  })
  .strict();
export type RecordProbeRequest = z.infer<typeof RecordProbeRequest>;
